// JOIN add/remove planner. Joins are space-separated (no commas), so the
// list-helper from `applyStructuralEdit.lists.ts` doesn't apply directly —
// we build the JOIN keyword + target text + ON/USING clause ourselves.
//
// PRD: Ralph/SemanticResolverEdit/PRD.md § Slice: Structural Edit Applicator — add/remove.

import type { JoinKind, PlannedByteEdit, StructuralEdit, AddJoinClause } from '../types';

import { skipContextFor } from '../classify/tokenSkip';

import type { EditCtx } from './applyStructuralEdit';
import {
  mergeSameOffsetInserts,
  planAliasRelations,
  withRenamedQualifiers,
} from './applyStructuralEdit.aliasRelations';
import { planQualifyColumns } from './applyStructuralEdit.qualifyColumns';
import { deleteEdit, insertEdit, trimTrailingWs } from './applyStructuralEdit.shared';

const JOIN_KEYWORDS: ReadonlySet<string> = new Set([
  'JOIN', 'APPLY', 'INNER', 'LEFT', 'RIGHT', 'FULL', 'OUTER', 'CROSS',
  'NATURAL', 'LATERAL', 'STRAIGHT_JOIN',
]);

// Skip backward over whitespace, `/* */` block comments, `--` (and `#` when
// `supportsHashComment`) line comments, and single-quoted strings, starting
// from index `i` (inclusive of source[i]). Returns the index of the first
// non-skippable byte (so source[result] is real token text), or -1 when the
// scan reaches the start of the buffer. Mirrors relationSlotKind's backward
// comment-skip (applyStructuralEdit.cte.ts) so a JOIN/ON-looking token inside
// a comment or string in the back-scan region is never mistaken for a join
// keyword.
function skipBackwardWsAndComments(
  source: string,
  i: number,
  supportsHashComment: boolean,
): number {
  for (;;) {
    while (i >= 0 && /\s/.test(source[i])) i--;
    if (i < 0) return -1;
    // Backward over a `/* … */` block comment (scan `*/` → `/*`).
    if (i >= 1 && source[i] === '/' && source[i - 1] === '*') {
      i -= 2;
      while (i >= 1 && !(source[i] === '*' && source[i - 1] === '/')) i--;
      i -= 2;
      continue;
    }
    // Backward over a single-quoted string (scan a closing `'` back to its
    // opening `'`, honouring doubled `''` escapes).
    if (source[i] === '\'') {
      let j = i - 1;
      while (j >= 0) {
        if (source[j] === '\'') {
          if (j >= 1 && source[j - 1] === '\'') { j -= 2; continue; }
          break;
        }
        j--;
      }
      i = j - 1;
      continue;
    }
    // Backward over a `-- …` / `# …` line comment. A line comment always
    // ends at the newline already consumed as whitespace, so the earliest
    // `--`/`#` marker on the current line (left of i) opens a comment that
    // contains i. Jump before that marker (mirrors the block-comment arm;
    // conservative — a marker inside a string on that line over-skips, only
    // ever a safe false-negative for keyword detection).
    const lineStart = source.lastIndexOf('\n', i) + 1;
    let m = -1;
    for (let k = lineStart; k < i; k++) {
      if ((supportsHashComment && source[k] === '#')
        || (source[k] === '-' && source[k + 1] === '-')) { m = k; break; }
    }
    if (m >= 0) { i = m - 1; continue; }
    return i;
  }
}

// Walk back from a join's range.startOffset (which sits AFTER the JOIN
// keyword token, per the extractor's readJoinClause contract) to find the
// first byte of the join keyword chain — i.e. the leading prefix keyword
// (INNER, LEFT, CROSS, …) or JOIN itself when there is no prefix. The
// whitespace-skips are comment/string aware so a JOIN-looking word inside a
// comment/string in the back-scan region is not mistaken for a join keyword.
function findJoinKeywordStart(
  source: string,
  joinRangeStart: number,
  supportsHashComment: boolean,
): number {
  // The range starts at the byte AFTER the keyword (a space or paren). Skip
  // ws + comments going backward, then walk back over the JOIN keyword.
  let after = skipBackwardWsAndComments(source, joinRangeStart - 1, supportsHashComment);
  if (after < 0) return 0;
  let i = after + 1;
  while (i > 0) {
    let j = i;
    while (j > 0 && /[A-Za-z_]/.test(source[j - 1])) j--;
    if (j === i) break;
    const word = source.slice(j, i).toUpperCase();
    if (!JOIN_KEYWORDS.has(word)) break;
    i = j;
    // Skip ws + comments back to the previous keyword (or the predecessor).
    after = skipBackwardWsAndComments(source, i - 1, supportsHashComment);
    if (after < 0) return 0;
    i = after + 1;
  }
  return i;
}

// Test-only: exposes findJoinKeywordStart's comment/string-aware back-scan in
// isolation. The public apply path masks (via planRemoveJoin's prevSiblingEnd
// clamp) or upstream-contaminates (extractQueryStructure is itself
// comment-blind) every shape that would otherwise exercise the back-scan, so
// SED-12's hardening is locked here directly. Not part of the runtime API.
export function __findJoinKeywordStartForTests(
  source: string,
  joinRangeStart: number,
  supportsHashComment: boolean,
): number {
  return findJoinKeywordStart(source, joinRangeStart, supportsHashComment);
}

// Map a logical JoinKind back to a keyword sequence to emit. The user's
// dominant-casing preference is tracked at the modify slice; for the
// add/remove slice we emit upper-case which is the typical default.
function joinKeyword(kind: JoinKind): string {
  switch (kind) {
    case 'inner': return 'INNER JOIN';
    case 'left': return 'LEFT JOIN';
    case 'right': return 'RIGHT JOIN';
    case 'full': return 'FULL JOIN';
    case 'cross': return 'CROSS JOIN';
    case 'lateral': return 'LATERAL';
    case 'cross-apply': return 'CROSS APPLY';
    case 'outer-apply': return 'OUTER APPLY';
  }
}

function buildJoinText(clause: AddJoinClause): string {
  const head = joinKeyword(clause.kind);
  const target = clause.targetText;
  const tail = buildJoinTail(clause);
  return tail ? `${head} ${target} ${tail}` : `${head} ${target}`;
}

function buildJoinTail(clause: AddJoinClause): string {
  if (clause.kind === 'cross' || clause.kind === 'cross-apply' || clause.kind === 'outer-apply') {
    // CROSS-style joins do not take ON / USING.
    return '';
  }
  if (clause.onText) return `ON ${clause.onText}`;
  if (clause.usingColumns && clause.usingColumns.length > 0) {
    return `USING (${clause.usingColumns.join(', ')})`;
  }
  return '';
}

export function planJoinEdit(
  ctx: EditCtx,
  edit: Extract<StructuralEdit, { op: 'add-join' | 'remove-join' }>,
): readonly PlannedByteEdit[] {
  if (edit.op === 'add-join') {
    // Alias the base relation(s) first: the join's ON text and the bare-column
    // qualifiers then use the aliases the planner actually applied.
    const aliasing = planAliasRelations(ctx, edit.clause.aliasRelations ?? []);
    const clause = withRenamedQualifiers(edit.clause, aliasing.renamed, skipContextFor(ctx.options));
    return mergeSameOffsetInserts([
      ...aliasing.edits,
      ...planAddJoin(ctx, { ...edit, clause }),
      ...planQualifyColumns(ctx, clause.qualifyColumns ?? []),
    ]);
  }
  return planRemoveJoin(ctx, edit);
}

// Trim trailing whitespace from a join's range to find the position just
// past the last non-whitespace byte of the predicate. This is where new
// joins should be inserted (so that we don't double-up on the trailing
// whitespace that the join's range carries up to the next clause keyword).
function joinTextEnd(source: string, joinEnd: number, lowerBound: number): number {
  return trimTrailingWs(source, lowerBound, joinEnd);
}

function planAddJoin(
  ctx: EditCtx,
  edit: Extract<StructuralEdit, { op: 'add-join' }>,
): readonly PlannedByteEdit[] {
  const joins = ctx.structure.joins ?? [];
  const fromItems = ctx.structure.fromItems ?? [];
  const text = buildJoinText(edit.clause);
  const stmtStart = ctx.statementInfo.range.startOffset;

  if (joins.length === 0) {
    if (fromItems.length === 0) {
      return [insertEdit(ctx, stmtStart, ` ${text}`, 'add-join:empty-from')];
    }
    const offset = fromItems[fromItems.length - 1].range.endOffset;
    return [insertEdit(ctx, offset, ` ${text}`, 'add-join:append-after-from')];
  }

  const last = joins.length - 1;
  if (edit.afterIndex === undefined || edit.afterIndex >= last) {
    const at = joinTextEnd(ctx.source, joins[last].range.endOffset, stmtStart);
    return [insertEdit(ctx, at, ` ${text}`, 'add-join:append')];
  }
  if (edit.afterIndex < 0) {
    const anchor = fromItems.length
      ? fromItems[fromItems.length - 1].range.endOffset
      : findJoinKeywordStart(
        ctx.source,
        joins[0].range.startOffset,
        skipContextFor(ctx.options).supportsHashComment,
      );
    return [insertEdit(ctx, anchor, ` ${text}`, 'add-join:before-first')];
  }
  // Middle: insert AFTER the trimmed end of joins[afterIndex] and BEFORE the
  // following join's keyword. The range carries trailing whitespace up to
  // the next keyword's start; trimming back finds the byte just past 'b.id'.
  const at = joinTextEnd(ctx.source, joins[edit.afterIndex].range.endOffset, stmtStart);
  return [insertEdit(ctx, at, ` ${text}`, 'add-join:middle')];
}

function planRemoveJoin(
  ctx: EditCtx,
  edit: Extract<StructuralEdit, { op: 'remove-join' }>,
): readonly PlannedByteEdit[] {
  const joins = ctx.structure.joins ?? [];
  if (edit.index < 0 || edit.index >= joins.length) return [];
  const target = joins[edit.index];
  // The join's keyword (and any prefix words) sit BEFORE its range.startOffset;
  // walk back to find the first byte of the JOIN keyword chain. The whitespace
  // at the end of the range is part of the next-clause separator, so we
  // trim it back to the last byte of the predicate when the join is the last.
  const stmtStart = ctx.statementInfo.range.startOffset;
  const start = findJoinKeywordStart(
    ctx.source,
    target.range.startOffset,
    skipContextFor(ctx.options).supportsHashComment,
  );
  // Always trim trailing whitespace from the deletion end so the inter-join
  // separator before the next sibling is preserved. (For the last join the
  // trailing whitespace is not part of any sibling separator either, so
  // trimming is safe.)
  const end = joinTextEnd(ctx.source, target.range.endOffset, stmtStart);
  // Pull the leading whitespace into the deletion when there's a previous
  // sibling — so removing a join in a chain doesn't leave a phantom space.
  let deleteStart = start;
  while (deleteStart > stmtStart && /\s/.test(ctx.source[deleteStart - 1])) deleteStart--;
  // …but only if the byte before that whitespace is the previous join's last
  // predicate byte (we don't want to consume the FROM-target's trailing space).
  const prevSiblingEnd = previousSiblingEnd(ctx, edit.index);
  if (deleteStart < prevSiblingEnd) deleteStart = prevSiblingEnd;
  return [deleteEdit(ctx, deleteStart, end, 'remove-join')];
}

function previousSiblingEnd(ctx: EditCtx, index: number): number {
  const joins = ctx.structure.joins ?? [];
  if (index > 0) {
    return joinTextEnd(
      ctx.source,
      joins[index - 1].range.endOffset,
      ctx.statementInfo.range.startOffset,
    );
  }
  const fromItems = ctx.structure.fromItems ?? [];
  if (fromItems.length > 0) return fromItems[fromItems.length - 1].range.endOffset;
  return ctx.statementInfo.range.startOffset;
}
